import { Link } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useSEO, mergeKeywordSets, COMPETITIVE_KEYWORD_CLUSTERS } from '../lib/seo';
import { StockHeroStrip, StockImage, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';

export default function AiCouplesTherapy() {
  useSEO({
    title: 'AI Couples Therapy: Coaching vs Licensed Care | TENSION',
    description:
      'Understand AI couples therapy style coaching: what apps can help with, limits, and how TENSION fits alongside professional support.',
    keywords: mergeKeywordSets(
      COMPETITIVE_KEYWORD_CLUSTERS.communication as unknown as string[],
      COMPETITIVE_KEYWORD_CLUSTERS.core as unknown as string[],
      ['ai couples therapy', 'couples therapy app', 'relationship coaching ai'],
    ),
    canonical: '/ai-couples-therapy',
    ogType: 'website',
    twitterCard: 'summary_large_image',
  });

  return (
    <WarmMarketingBackdrop>
      <div className="mx-auto max-w-5xl space-y-10 px-4 py-10 md:py-14">
        <header className="grid gap-6 overflow-hidden rounded-3xl border border-fuchsia-200/60 bg-white/90 shadow-xl md:grid-cols-[1fr_0.95fr] md:items-center">
          <div className="p-6 md:p-10">
            <h1 className="text-3xl font-black tracking-tight text-neutral-900 md:text-4xl">
              AI couples therapy support: scope and limits
            </h1>
            <p className="mt-3 max-w-2xl text-neutral-600 md:text-lg">
              Many couples search for AI couples therapy. Useful tools focus on communication coaching and conflict
              prevention. Licensed therapy is different and remains essential when indicated.
            </p>
          </div>
          <StockImage
            stockKey="supportCalm"
            width={800}
            height={560}
            priority
            alt="Supportive professional context — illustrative stock photo"
            className="h-52 w-full object-cover md:h-full md:min-h-[240px] md:rounded-none md:rounded-r-3xl"
          />
        </header>

        <StockHeroStrip stockKey="handsWalking" alt="Couples connection — illustrative imagery" />

        <Card id="trust" className="border-fuchsia-200/80 bg-white/95 p-6 shadow-md md:p-8">
          <h2 className="text-lg font-semibold text-neutral-900">Our positioning</h2>
          <p className="mt-2 text-sm leading-relaxed text-neutral-700">
            TENSION provides structured tension insight and message-level coaching. We do not claim to replace a
            licensed therapist. For crisis or abuse, contact local emergency resources or a qualified professional.
          </p>
        </Card>

        <div className="grid gap-4 md:grid-cols-2">
          <Card className="border-fuchsia-100/90 bg-white/95 p-6 shadow-md">
            <h2 className="text-lg font-semibold text-neutral-900">Where AI coaching helps</h2>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-neutral-700">
              <li>Rewriting heated messages before you send.</li>
              <li>Building daily communication habits.</li>
              <li>Spotting tension trends over time.</li>
            </ul>
          </Card>
          <Card className="border-fuchsia-100/90 bg-white/95 p-6 shadow-md">
            <h2 className="text-lg font-semibold text-neutral-900">Try next</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/features/conversation-micro-coach">
                <Button className="bg-gradient-to-r from-fuchsia-600 to-violet-600 text-white shadow-md">
                  Conversation Micro-Coach
                </Button>
              </Link>
              <Link to="/blog/ai-couples-therapy-what-it-is-and-isnt">
                <Button variant="outline" className="border-fuchsia-200 bg-white/90">
                  Read the article
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </WarmMarketingBackdrop>
  );
}
